/* Banking Portal Shell — reorganized Feb 2026.
   37 flat tabs → 2 top-level + 9 collapsible groups matching the admin
   console pattern. Same dark-slate aesthetic preserved.
*/
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Banknote, LayoutDashboard, Activity, Webhook, FileWarning, LineChart,
  LogOut, Building2, ShieldCheck, Download, Award,
  Map, Scale, Shield, Eye, ShieldAlert, FileSearch,
  User, Bell, TrendingUp, Bug, Cpu, Globe, Beaker, Network,
  KeyRound, Fingerprint, CalendarCheck2, Sparkles, Users, Plug, Sigma,
  Wallet, ChevronDown, AlertTriangle,
} from "lucide-react";
import { Button } from "./ui/button";

// ─────────────────────────────────────────────────────────────────────────
// 2 top-level entries · 9 collapsible groups
// ─────────────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  // Top-level: Overview (single)
  {
    id: "overview", is_single: true,
    to: "/banking", end: true,
    label: "Overview", icon: LayoutDashboard,
  },
  // Top-level: Diligence Packet (single, high-value examiner export)
  {
    id: "diligence", is_single: true,
    to: "/banking/diligence", end: true,
    label: "Diligence Packet", icon: Award,
  },

  // Group 1 · Money Movement
  {
    id: "money", label: "Money Movement", icon: Wallet,
    children: [
      { to: "/banking/money-map",      label: "Money Map",      icon: Map },
      { to: "/banking/watchtower",     label: "Live Watchtower",icon: Activity },
      { to: "/banking/reconciliation", label: "Reconciliation", icon: Scale },
      { to: "/banking/liquidity",      label: "Liquidity",      icon: Sigma },
      { to: "/banking/transactions",   label: "Transactions",   icon: Activity },
    ],
  },

  // Group 2 · AML & Sanctions
  {
    id: "aml", label: "AML & Sanctions", icon: ShieldAlert,
    children: [
      { to: "/banking/bsa-officer",        label: "BSA Officer",       icon: Scale },
      { to: "/banking/aml-typologies",     label: "AML Typologies",    icon: ShieldAlert },
      { to: "/banking/sanctions-coverage", label: "Sanctions Coverage",icon: Globe },
      { to: "/banking/policy-trace",       label: "Policy Trace",      icon: FileSearch },
      { to: "/banking/pre-auth-simulator", label: "Pre-Auth Simulator",icon: Beaker },
    ],
  },

  // Group 3 · Customer Risk
  {
    id: "customer", label: "Customer Risk", icon: Users,
    children: [
      { to: "/banking/customer-profile",     label: "Customer Profile",     icon: User },
      { to: "/banking/crr",                  label: "CRR + EDD",            icon: Users },
      { to: "/banking/network-risk",         label: "Network Risk Graph",   icon: Network },
    ],
  },

  // Group 4 · Predictive & Behavioral
  {
    id: "predictive", label: "Predictive & Alerts", icon: Sparkles,
    children: [
      { to: "/banking/predictive-forecast", label: "Predictive Forecast", icon: Sparkles },
      { to: "/banking/behavioral",          label: "Behavioral Baselines",icon: TrendingUp },
      { to: "/banking/alerts-push",         label: "Push Alerts",         icon: Bell },
    ],
  },

  // Group 5 · Security & Cyber
  {
    id: "security", label: "Security & Cyber", icon: Shield,
    children: [
      { to: "/banking/security-posture",   label: "Security Posture",     icon: ShieldAlert },
      { to: "/banking/encryption-posture", label: "Encryption (NYDFS)",   icon: KeyRound },
      { to: "/banking/auth-posture",       label: "Auth (FFIEC)",         icon: Fingerprint },
      { to: "/banking/defense-posture",    label: "DDoS / WAF Defense",   icon: Shield },
      { to: "/banking/cyber",              label: "Cyber Deep-Dive",      icon: Bug },
    ],
  },

  // Group 6 · Compliance & Governance
  {
    id: "compliance", label: "Compliance & Governance", icon: Scale,
    children: [
      { to: "/banking/regulatory-calendar", label: "Reg Calendar",        icon: CalendarCheck2 },
      { to: "/banking/model-risk",          label: "Model Risk (SR 11-7)",icon: Cpu },
      { to: "/banking/fair-lending",        label: "Fair Lending",        icon: Scale },
      { to: "/banking/nacha",               label: "NACHA",               icon: Banknote },
      { to: "/banking/resilience",          label: "GLBA + Resilience",   icon: ShieldCheck },
      { to: "/banking/regulatory",          label: "Regulatory",          icon: Shield },
      { to: "/banking/privacy",             label: "Privacy",             icon: Eye },
    ],
  },

  // Group 7 · Operations & Vendors
  {
    id: "ops", label: "Operations & Vendors", icon: AlertTriangle,
    children: [
      { to: "/banking/ops-defense",         label: "Ops Defense",          icon: ShieldCheck },
      { to: "/banking/vendor-concentration",label: "Vendor Concentration", icon: Plug },
      { to: "/banking/onboarding-funnel",   label: "Onboarding Funnel",    icon: TrendingUp },
      { to: "/banking/examiner-links",      label: "Examiner Links",       icon: Award },
    ],
  },

  // Group 8 · Integrations & Reports
  {
    id: "integrations", label: "Integrations & Reports", icon: Plug,
    children: [
      { to: "/banking/webhooks",   label: "Webhooks",    icon: Webhook },
      { to: "/banking/chargebacks",label: "Chargebacks", icon: FileWarning },
      { to: "/banking/api-health", label: "API Health",  icon: LineChart },
      { to: "/banking/reports",    label: "Reports",     icon: Download },
    ],
  },
];

export default function BankingPortalShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [meta, setMeta] = useState(null);

  // Track expand/collapse per group. By default a group is auto-expanded
  // when the current route is one of its children.
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/partner/banking/whoami")
      .then(r => setMeta(r.data))
      .catch(() => {});
  }, []);

  const doLogout = async () => {
    try { await logout(); } finally { nav("/banking/login", { replace: true }); }
  };

  // Pre-compute which group contains the active route.
  const activeGroupId = useMemo(() => {
    for (const g of NAV_GROUPS) {
      if (g.is_single) {
        if (location.pathname === g.to) return g.id;
        continue;
      }
      const hit = (g.children || []).some(c =>
        location.pathname === c.to || location.pathname.startsWith(c.to + "/")
      );
      if (hit) return g.id;
    }
    return null;
  }, [location.pathname]);

  const renderGroup = (g) => {
    if (g.is_single) {
      const Icon = g.icon;
      return (
        <NavLink
          key={g.id}
          to={g.to}
          end={g.end}
          data-testid={`banking-nav-${g.id}`}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition ${
              isActive ? "bg-sky-500/15 text-sky-100"
                       : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
            }`
          }
        >
          <Icon className="w-4 h-4" />
          {g.label}
        </NavLink>
      );
    }

    const isOn = activeGroupId === g.id;
    const expandKey = `__group_${g.id}`;
    const isExpanded = expanded[expandKey] !== undefined ? expanded[expandKey] : isOn;
    const Icon = g.icon;

    return (
      <div key={g.id} data-testid={`banking-nav-group-${g.id}`}>
        <button
          type="button"
          onClick={() => setExpanded(p => ({ ...p, [expandKey]: !isExpanded }))}
          data-testid={`banking-nav-group-toggle-${g.id}`}
          aria-expanded={isExpanded}
          className={`group relative w-full flex items-center gap-2.5 rounded-md px-3 pr-8 py-2 text-sm transition ${
            isOn ? "bg-slate-800/70 text-white font-medium"
                 : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
          }`}
        >
          <Icon className="w-4 h-4" />
          <span className="flex-1 text-left">{g.label}</span>
          <ChevronDown
            className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {isExpanded && (
          <div
            className="mt-0.5 ml-3 pl-3 border-l border-slate-700/70 space-y-0.5"
            data-testid={`banking-nav-group-children-${g.id}`}
          >
            {(g.children || []).map(c => {
              const ChildIcon = c.icon;
              return (
                <NavLink
                  key={c.to}
                  to={c.to}
                  data-testid={`banking-nav-${c.to.replace(/[^a-z0-9]/gi, "-").toLowerCase().replace(/^-/, "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[12.5px] transition ${
                      isActive ? "bg-sky-500/15 text-sky-100 font-medium"
                               : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                    }`
                  }
                >
                  <ChildIcon className="w-3.5 h-3.5" />
                  <span className="flex-1 truncate">{c.label}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="banking-portal-shell" className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-100 flex flex-col">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-sky-500/20 grid place-items-center">
              <Banknote className="w-4 h-4 text-sky-300" />
            </div>
            <div>
              <div className="text-sm font-semibold">verqo · Banking</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Partner Portal</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="banking-sidebar">
          {NAV_GROUPS.map(renderGroup)}
        </nav>

        <div className="px-3 py-3 border-t border-slate-800 space-y-1.5">
          {meta && (
            <div className="px-2 py-2 rounded-md bg-slate-800/40 text-xs text-slate-300 leading-snug" data-testid="banking-org-info">
              <div className="flex items-center gap-1.5 text-slate-200 font-medium">
                <Building2 className="w-3.5 h-3.5" /> {meta.partner_org_name || "Unattached"}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 truncate">{user?.email}</div>
              {meta.partner_org_status === "active" && (
                <div className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Org active
                </div>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            onClick={doLogout}
            data-testid="banking-logout-btn"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
